import { Button, Center, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const formatErrorMessage = (error: unknown) => {
	if (error instanceof Error) {
		return error.message;
	}

	if (typeof error === 'string') {
		return error;
	}

	return null;
};

type ErrorFallbackProps = {
	error?: unknown;
	onRetry?: () => void;
	fullHeight?: boolean;
	showErrorDetails?: boolean;
	extraContent?: ReactNode;
};

export function ErrorFallback({
	error,
	onRetry,
	fullHeight = true,
	showErrorDetails = true,
	extraContent,
}: ErrorFallbackProps) {
	const { t } = useTranslation();
	const isOffline = typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine;
	const errorMessage = formatErrorMessage(error);

	const handleRetry = onRetry ?? (() => window.location.reload());
	const height = fullHeight ? '100vh' : undefined;

	return (
		<Center h={height} px="md">
			<Stack align="center" gap="sm" p="md">
				<Text size="lg" fw={600} ta="center">
					{isOffline ? t('common.errors.offlineQueryTitle') : t('common.errors.queryLoadTitle')}
				</Text>
				<Text ta="center">{isOffline ? t('common.errors.offlineQueryBody') : t('common.errors.queryLoadBody')}</Text>
				{showErrorDetails && errorMessage ? (
					<Text c="dimmed" size="sm" ta="center">
						{errorMessage}
					</Text>
				) : null}
				{extraContent}
				<Button onClick={handleRetry}>{t('common.errors.retry')}</Button>
			</Stack>
		</Center>
	);
}
